package com.example.mystybag.core.security

import kotlinx.datetime.Clock
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import kotlin.io.encoding.Base64
import kotlin.io.encoding.ExperimentalEncodingApi

/**
 * Returns true if the JWT is expired or invalid (e.g. missing/invalid payload or exp claim).
 * Uses a 60-second buffer: token is considered expired 60 seconds before its actual exp time.
 */
@OptIn(ExperimentalEncodingApi::class)
fun isJwtExpired(jwt: String): Boolean {
    if (jwt.isBlank()) return true
    val parts = jwt.split(".")
    if (parts.size < 2) return true
    return try {
        val base64 = Base64.UrlSafe.withPadding(Base64.PaddingOption.ABSENT_OPTIONAL)
        val payloadBytes = base64.decode(parts[1])
        val payloadStr = payloadBytes.decodeToString()
        val json = Json.parseToJsonElement(payloadStr).jsonObject
        val exp = json["exp"]?.jsonPrimitive?.contentOrNull?.toLongOrNull() ?: return true
        val nowSeconds = Clock.System.now().toEpochMilliseconds() / 1000
        exp < nowSeconds + 60L // 60 second buffer: consider expired 1 min before actual exp
    } catch (_: Exception) {
        true
    }
}

/**
 * Returns the value of a string [claim] from the JWT payload, or null if the token is
 * malformed or the claim is missing. Does NOT verify the signature — only use for reading
 * claims from a token whose authenticity is verified elsewhere (e.g. on the backend).
 *
 * Used to recover the `email` claim from an Apple identity token on subsequent Sign in with
 * Apple logins, where `ASAuthorizationAppleIDCredential.email` is null but Apple still includes
 * the email inside the JWT.
 */
@OptIn(ExperimentalEncodingApi::class)
fun extractJwtClaimString(jwt: String, claim: String): String? {
    if (jwt.isBlank()) return null
    val parts = jwt.split(".")
    if (parts.size < 2) return null
    return try {
        val base64 = Base64.UrlSafe.withPadding(Base64.PaddingOption.ABSENT_OPTIONAL)
        val payloadStr = base64.decode(parts[1]).decodeToString()
        val json = Json.parseToJsonElement(payloadStr).jsonObject
        json[claim]?.jsonPrimitive?.contentOrNull?.takeIf { it.isNotBlank() }
    } catch (_: Exception) {
        null
    }
}
